import React, { useEffect, useState } from 'react';
import { Button, TextField, Select, MenuItem, FormControl, InputLabel, IconButton } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Account, JournalEntryLine, JournalEntryCreate as JournalEntryCreateType, Segment } from '../../interfaces/accounting.interface';
import { JournalEntryService } from '../../services/journal-entry.service';
import { AccountService } from '../../services/account.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { SegmentService } from '../../services/segment.service';
import { SegmentSettingsService } from '../../services/segment-settings.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';

export const isSegmentRequired = (accountId: string, accounts: Account[]): boolean =>
    !!accountId && accounts.find(a => a.id === accountId)?.segmentFieldPolicy === 'REQUIRED';

const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left' as const,
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

interface LineItem {
    accountId: string;
    entryType: 'DEBIT' | 'CREDIT';
    amount: number;
    description: string;
    segmentId?: string;
}

const JournalEntryCreate: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [segments, setSegments] = useState<Segment[]>([]);
    const [defaultSegmentId, setDefaultSegmentId] = useState<string>('');
    const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [description, setDescription] = useState<string>('');
    const [lines, setLines] = useState<LineItem[]>([
        { accountId: '', entryType: 'DEBIT', amount: 0, description: '' },
        { accountId: '', entryType: 'CREDIT', amount: 0, description: '' }
    ]);

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                setAccounts(await AccountService.getAllAccounts());
            } catch (err) {
                console.error('Error fetching accounts:', err);
            }
        };
        const fetchSegments = async () => {
            try {
                const res = await SegmentService.getByInstitution();
                const all: Segment[] = res.content ?? [];
                setSegments(all.filter(s => s.active !== false));
            } catch (err) {
                console.error('Error fetching segments:', err);
            }
        };
        fetchAccounts();
        fetchSegments();
        SegmentSettingsService.get()
            .then(s => setDefaultSegmentId(s.defaultSegmentId ?? ''))
            .catch(() => setDefaultSegmentId(''));
    }, []);

    const totalDebits = lines
        .filter(l => l.entryType === 'DEBIT')
        .reduce((sum, l) => sum + Number(l.amount || 0), 0);

    const totalCredits = lines
        .filter(l => l.entryType === 'CREDIT')
        .reduce((sum, l) => sum + Number(l.amount || 0), 0);

    const handleAddLine = () => {
        setLines([...lines, { accountId: '', entryType: 'DEBIT', amount: 0, description: '' }]);
    };

    const handleRemoveLine = (index: number) => {
        if (lines.length <= 2) return;
        setLines(lines.filter((_, i) => i !== index));
    };

    const handleLineChange = (index: number, field: keyof LineItem, value: string | number) => {
        const updated = [...lines];
        (updated[index] as any)[field] = value;
        setLines(updated);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (Math.abs(totalDebits - totalCredits) > 0.001) {
            toast.showError('Total debits must equal total credits');
            return;
        }

        for (const line of lines) {
            if (!line.accountId || line.amount <= 0) {
                toast.showError('All lines must have an account selected and a positive amount');
                return;
            }
        }

        if (lines.some(l => isSegmentRequired(l.accountId, accounts) && !l.segmentId)) {
            toast.showApiError(null, 'Select a segment for every line whose account requires one');
            return;
        }

        try {
            const institutionId = AuthService.getSelectedInstitution();
            const entryLines: JournalEntryLine[] = lines.map(l => ({
                accountId: l.accountId,
                entryType: l.entryType,
                amount: Number(l.amount),
                description: l.description || undefined,
                segmentId: l.segmentId || undefined
            }));

            const entryData: JournalEntryCreateType = {
                date,
                description: description || undefined,
                institutionId,
                lines: entryLines
            };

            await JournalEntryService.createEntry(entryData);
            navigate('/accounting/journal-entries');
        } catch (err) {
            console.error('Error creating journal entry:', err);
            toast.showApiError(err, 'Failed to create journal entry');
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Create Journal Entry</h2>
                <Button variant="outlined" onClick={() => navigate('/accounting/journal-entries')}>
                    Back to List
                </Button>
            </div>

            <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                    <TextField
                        label="Date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        required
                        InputLabelProps={{ shrink: true }}
                        style={{ width: '200px' }}
                    />
                    <TextField
                        label="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        style={{ flex: 1 }}
                    />
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3>Entry Lines</h3>
                    <Button variant="outlined" startIcon={<Add />} onClick={handleAddLine}>
                        Add Line
                    </Button>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
                    <thead>
                        <tr>
                            <th style={tableHeaderStyle}>Account</th>
                            <th style={tableHeaderStyle}>Type</th>
                            <th style={tableHeaderStyle}>Amount</th>
                            <th style={tableHeaderStyle}>Description</th>
                            <th style={tableHeaderStyle}>Segment</th>
                            <th style={tableHeaderStyle}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line, index) => (
                            <tr key={index}>
                                <td style={tableCellStyle}>
                                    <AccountAutocomplete
                                        data-testid={`je-account-${index}`}
                                        accounts={accounts}
                                        value={line.accountId}
                                        label="Account"
                                        placeholder="Select account"
                                        onChange={(accountId) => {
                                            handleLineChange(index, 'accountId', accountId);
                                            if (isSegmentRequired(accountId, accounts) && !line.segmentId && defaultSegmentId) {
                                                handleLineChange(index, 'segmentId', defaultSegmentId);
                                            }
                                        }}
                                    />
                                </td>
                                <td style={tableCellStyle}>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={line.entryType}
                                            onChange={(e) => handleLineChange(index, 'entryType', e.target.value)}
                                        >
                                            <MenuItem value="DEBIT">DEBIT</MenuItem>
                                            <MenuItem value="CREDIT">CREDIT</MenuItem>
                                        </Select>
                                    </FormControl>
                                </td>
                                <td style={tableCellStyle}>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={line.amount}
                                        onChange={(e) => handleLineChange(index, 'amount', Number(e.target.value))}
                                        inputProps={{ min: 0, step: '0.01' }}
                                        fullWidth
                                    />
                                </td>
                                <td style={tableCellStyle}>
                                    <TextField
                                        size="small"
                                        value={line.description}
                                        onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                                        fullWidth
                                    />
                                </td>
                                <td style={tableCellStyle}>
                                    <FormControl fullWidth size="small" error={isSegmentRequired(line.accountId, accounts) && !line.segmentId}>
                                        <Select
                                            data-testid={`je-segment-${index}`}
                                            value={line.segmentId ?? ''}
                                            onChange={(e) => {
                                                const val = e.target.value as string;
                                                handleLineChange(index, 'segmentId', val);
                                            }}
                                            displayEmpty
                                        >
                                            <MenuItem value="">{isSegmentRequired(line.accountId, accounts) ? 'Select segment…' : '(default)'}</MenuItem>
                                            {segments.map(s => (
                                                <MenuItem key={s.id} value={s.id!}>{s.code} — {s.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </td>
                                <td style={tableCellStyle}>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveLine(index)}
                                        disabled={lines.length <= 2}
                                        aria-label="delete"
                                    >
                                        <Delete />
                                    </IconButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Running Totals */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '32px', marginBottom: '24px', fontSize: '16px' }}>
                    <div>
                        <strong>Total Debits:</strong> {totalDebits.toFixed(2)}
                    </div>
                    <div>
                        <strong>Total Credits:</strong> {totalCredits.toFixed(2)}
                    </div>
                    <div style={{ color: Math.abs(totalDebits - totalCredits) > 0.001 ? 'red' : 'green' }}>
                        <strong>Difference:</strong> {Math.abs(totalDebits - totalCredits).toFixed(2)}
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button variant="outlined" onClick={() => navigate('/accounting/journal-entries')}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        Create Entry
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default JournalEntryCreate;
